import React from "react";
import { Typography } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import AppState from "../../store/AppState";
import {
  selectIsModalOpen,
  selectModalVariable,
} from "../../store/modal/ModalSelectors";
import { setModalState } from "../../store/modal/ModalActions";
import { useIntl } from "react-intl";
import CustomDialog from "../common/DialogContainer";
import ButtonComponent from "../common/ButtonComponent";
import { makeStyles, createStyles } from "@material-ui/core";
import {
  loadInitialProducts,
  loadProductIdsFromCategory,
  setCurrentPage,
} from "../../store/product-list/ProductListActions";
import {
  selectCurrentStoreId,
  selectCurrentLocale,
} from "../../store/store-list/StoreListSelectors";
import { selectCurrentCatalogId } from "../../store/catalog/CatalogSelectors";
import { selectStoreListIds } from "../../store/store-list/StoreListSelectors";
import { selectIsCategoryProductsV2Available } from "../../store/adapter-config/AdapterConfigSelectors";
import AppEnv from "../../utils/AppEnv";

export const ReloadCategoryModalId = "ReloadCategoryModal";

const useStyles = makeStyles(() =>
  createStyles({
    dialogAction: {
      width: "100%",
      textAlign: "end",
      margin: "20px 20px 10px",
    },
    divMessage: {
      marginTop: "5px",
    },
  }),
);

const ReloadCategoryModal: React.FC = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const intl = useIntl();
  const categoryId = useSelector((state: AppState) =>
    selectModalVariable(state, ReloadCategoryModalId, "categoryId"),
  );
  const isModalOpen = useSelector((state: AppState) =>
    selectIsModalOpen(state, ReloadCategoryModalId),
  );
  const currentStoreId = useSelector(selectCurrentStoreId) ?? "";
  const currentCatalogId = useSelector(selectCurrentCatalogId);
  const currentLocaleId = useSelector(selectCurrentLocale) ?? "";
  const storeListIds = useSelector(selectStoreListIds);
  const isCategoryProductsV2Available = useSelector((state: AppState) =>
    selectIsCategoryProductsV2Available(state),
  );

  const handleClose = () => {
    dispatch(setModalState(ReloadCategoryModalId, false));
  };

  const handleReloadLatest = () => {
    if (!categoryId || !currentCatalogId) {
      handleClose();
      return;
    }

    dispatch(setCurrentPage(1));
    // Reload the category products
    if (isCategoryProductsV2Available) {
      const payload = {
        categoryId,
        catalogId: currentCatalogId,
        localeCode: currentLocaleId,
        storeId: currentStoreId,
        page: 1,
        pageSize: AppEnv.PRODUCT_LIST_PAGE_SIZE ?? 24,
      };
      dispatch(loadInitialProducts(payload));
    } else {
      const payload = {
        categoryId,
        catalogId: currentCatalogId,
        localeCode: currentLocaleId,
        storeId: currentStoreId,
        storeListIds,
      };
      dispatch(loadProductIdsFromCategory(payload));
    }

    handleClose();
  };

  const reloadCategoryModalTitle = () => {
    return (
      <Typography variant="inherit">
        {intl.formatMessage({
          id: "reloadCategoryModal.modalTitle",
          defaultMessage: "Save Completed",
        })}
      </Typography>
    );
  };

  const reloadCategoryModalContent = () => {
    return (
      <div className={classes.divMessage}>
        <Typography variant="subtitle1">
          {intl.formatMessage({
            id: "reloadCategoryModal.instruction",
            defaultMessage:
              "The category has been saved. Would you like to reload the latest data or keep your current view?",
          })}
        </Typography>
      </div>
    );
  };

  const dialogActions = () => {
    return (
      <div className={classes.dialogAction}>
        <ButtonComponent
          color="secondaryButtonColorCTABlue"
          variant="outlined"
          onClick={handleClose}
        >
          {intl.formatMessage({
            id: "reloadCategoryModal.keepCurrentView",
            defaultMessage: "Keep Current View",
          })}
        </ButtonComponent>
        <ButtonComponent
          color="primary"
          variant="contained"
          onClick={handleReloadLatest}
        >
          {intl.formatMessage({
            id: "reloadCategoryModal.reloadLatest",
            defaultMessage: "Reload Latest",
          })}
        </ButtonComponent>
      </div>
    );
  };

  return (
    <CustomDialog
      open={isModalOpen}
      onClose={handleClose}
      title={reloadCategoryModalTitle()}
      actions={dialogActions()}
      maxWidth={"md"}
    >
      {reloadCategoryModalContent()}
    </CustomDialog>
  );
};

export default React.memo(ReloadCategoryModal);
